import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Payslip {
  id: string
  employee_id: string
  employee_name: string
  pay_period_start: string
  pay_period_end: string
  gross_salary: number
  net_salary: number
  status: string
}

interface Deduction {
  id: string
  employee_name: string
  deduction_type: string
  amount: number
  frequency: string
  is_pretax: boolean
}

export default function PayrollPage() {
  const [payslips, setPayslips] = useState<Payslip[]>([])
  const [deductions, setDeductions] = useState<Deduction[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [payRes, dedRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/payroll/payslips'),
      fetch(API_BASE + '/payroll/deductions'),
      fetch(API_BASE + '/payroll/statistics')
    ])
    if (payRes.ok) setPayslips((await payRes.json()).payslips || [])
    if (dedRes.ok) setDeductions((await dedRes.json()).deductions || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-blue-100 text-blue-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Payroll Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Run Payroll</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Payslips</div>
              <div className="text-2xl font-bold">{statistics.total_payslips}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Processed</div>
              <div className="text-2xl font-bold text-green-600">{statistics.processed_count}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Gross MTD</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_gross_mtd)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Net MTD</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_net_mtd)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Tax MTD</div>
              <div className="text-2xl font-bold">{formatCurrency(statistics.total_tax_mtd)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Payslips</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {payslips.slice(0, 12).map(ps => (
                  <tr key={ps.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{ps.employee_name}</div>
                      <div className="text-xs text-gray-500">{ps.employee_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{ps.pay_period_start}</div>
                      <div className="text-xs text-gray-500">to {ps.pay_period_end}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-green-600">{formatCurrency(ps.net_salary)}</div>
                      <div className="text-xs text-gray-500">Gross: {formatCurrency(ps.gross_salary)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ps.status)}`}>{ps.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Deductions</h3>
            <div className="space-y-3">
              {deductions.slice(0, 10).map(ded => (
                <div key={ded.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{ded.deduction_type}</span>
                    <span className="font-bold text-sm">{formatCurrency(ded.amount)}</span>
                  </div>
                  <div className="text-xs text-gray-500">{ded.employee_name}</div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500 capitalize">{ded.frequency}</span>
                    {ded.is_pretax && <span className="text-xs text-green-600">Pre-tax</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
